import { GeoJsonLayer, IconLayer, TextLayer, PathLayer } from "@deck.gl/layers";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import { ScatterplotLayer } from "@deck.gl/layers";
import { TileLayer } from "@deck.gl/geo-layers";
import { BitmapLayer } from "@deck.gl/layers";
import type { Feature, FeatureCollection, Polygon, MultiPolygon, LineString, Point } from "geojson";
import type { IncidentFeature, CampusZoneProperties, AirQualityPoint, IsochroneResult, BasinStation, DamReading, Attraction, HealthTambon, CctvCamera } from "@sikhio/shared";
import type { HeatPoint } from "../sim/trafficSim";
import { cameraIconAtlas, CAMERA_ICON_MAPPING } from "./cameraIcon";
import {
  buildFlowArrows,
  glyphRotationDeg,
  WATERWAY_COLOR,
  type WaterFlowArrow,
} from "./waterFlowArrows";
// Re-exported so existing importers of "./map/layers" keep working.
export { buildFlowArrows, type WaterFlowArrow } from "./waterFlowArrows";
import {
  classifyBuilding,
  buildingHeightMeters,
  heightColor,
  type BuildingProperties,
  type LandmarkKind,
} from "../lib/building";

// Re-export so existing `import { CctvCamera } from "./map/layers"` keeps working.
export type { CctvCamera } from "@sikhio/shared";
export interface ShuttleVehicle {
  id: string;
  line: string;
  lat: number;
  lng: number;
  bearing?: number;
  occupancy?: string;
}

/**
 * Properties of a road feature that the dashboard actually reads.
 *
 * This used to declare `nameEn`, `nameTh` and `oneway` as required, which
 * the shipped asset has not carried since scripts/slim-roads.mjs reduced the
 * file to its three read properties. A type that asserts fields the data does
 * not contain is worse than no type — it invites a future reader to
 * "use" `oneway` and get `undefined` at runtime.
 *
 *   priority → ROAD_STYLE lookup (below) and nothing else
 *   highway  → trafficSim classifyOsm()
 *   name     → trafficSim classifyOsm() road-name argument
 */
export interface ClassifiedRoadProps { name: string; highway: string; priority: number }

const ZONE_COLORS: Record<string, [number, number, number]> = {
  academic: [56, 189, 248],
  residential: [167, 139, 250],
  athletic: [245, 158, 11],
  park: [52, 211, 153],
  commercial: [14, 165, 233],
  service: [122, 132, 151],
  perimeter: [14, 165, 233],
};

type ZoneFeature = Feature<Polygon | MultiPolygon, CampusZoneProperties>;

export function campusBoundaryLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>,
  options: { extruded?: boolean; filled?: boolean; stroked?: boolean } = {},
) {
  return new GeoJsonLayer({
    id: "campus-boundary",
    data: collection as unknown as FeatureCollection,
    stroked: options.stroked ?? true,
    filled: options.filled ?? true,
    pickable: true,
    extruded: options.extruded ?? false,
    getFillColor: ((f: ZoneFeature) => {
      const z = ZONE_COLORS[f.properties.zoneType] ?? [120, 120, 120];
      return [z[0], z[1], z[2], 38] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineColor: ((f: ZoneFeature) => {
      const z = ZONE_COLORS[f.properties.zoneType] ?? [200, 200, 200];
      return [z[0], z[1], z[2], 200] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 1.5,
    lineWidthMinPixels: 1,
    getElevation: ((f: ZoneFeature) => f.properties.height ?? 0) as unknown as number,
  });
}

export function trafficHeatmapLayer(data: HeatPoint[]) {
  return new HeatmapLayer<HeatPoint>({
    id: "traffic-heatmap",
    data,
    getPosition: (d) => d.position,
    getWeight: (d) => d.weight,
    radiusPixels: 38,
    intensity: 1.2,
    threshold: 0.04,
    aggregation: "SUM",
    colorRange: [
      [56, 189, 248, 0],
      [56, 189, 248, 120],
      [167, 139, 250, 180],
      [245, 158, 11, 210],
      [248, 113, 113, 235],
      [239, 68, 68, 255],
    ],
  });
}

const INCIDENT_COLORS: Record<IncidentFeature["category"], [number, number, number]> = {
  "traffic-accident": [239, 68, 68],
  "traffic-congestion": [245, 158, 11],
  construction: [251, 191, 36],
  flooding: [56, 189, 248],
  waste: [168, 162, 158],
  lighting: [253, 224, 71],
  sidewalk: [167, 139, 250],
  drainage: [125, 211, 252],
  trees: [52, 211, 153],
  other: [148, 163, 184],
};

export function incidentLayer(id: string, data: IncidentFeature[]) {
  return new ScatterplotLayer<IncidentFeature>({
    id,
    data,
    getPosition: (d) => [d.lng, d.lat],
    getFillColor: (d) => {
      const c = INCIDENT_COLORS[d.category] ?? [200, 200, 200];
      return [...c, d.status === "resolved" ? 90 : 220] as [number, number, number, number];
    },
    getRadius: (d) => (d.severity === "high" ? 26 : d.severity === "medium" ? 18 : 12),
    radiusMinPixels: 4,
    radiusMaxPixels: 22,
    stroked: true,
    getLineColor: [10, 14, 20, 230],
    lineWidthMinPixels: 1,
    pickable: true,
  });
}

// ─── Campus building footprints (real OSM data) ──────────────────────────
// BuildingProperties, LandmarkKind, classifyBuilding, buildingHeightMeters,
// and finitePositive are imported from ../lib/building (pure, unit-tested).
export type { BuildingProperties, LandmarkKind };

// Landmark fill colours — one decision per category, legible in dark 3D.
// Reading guide: red=health, gold=culture, cyan=civic, violet=education,
//                amber=commerce, steel=industry, teal=office, sand=fabric.
const LANDMARK_COLOR: Record<NonNullable<LandmarkKind>, [number, number, number]> = {
  "ms-generic":  [165, 138, 112],  // warm sand — background residential fabric
  residential:   [210, 110,  65],  // terracotta — OSM-tagged houses / apartments
  commercial:    [245, 158,  11],  // amber — shops, retail, markets, F&B
  industrial:    [120, 130, 140],  // steel grey — warehouses, factories
  office:        [ 45, 170, 160],  // teal — offices, banks, post offices
  hotel:         [251, 191,  36],  // gold — tourism anchor
  temple:        [253, 224,  71],  // bright gold — cultural backbone
  church:        [253, 186, 116],  // pale peach — Christian worship
  mosque:        [134, 239, 172],  // mint green — Islamic worship
  government:    [ 56, 189, 248],  // sky-400 — city hall + public institutions
  police:        [ 34, 211, 238],  // cyan — safety infrastructure
  fire:          [251, 146,  60],  // orange — emergency response
  hospital:      [239,  68,  68],  // red — health anchor
  clinic:        [251, 113, 133],  // pink-red — clinics / doctors
  school:        [167, 139, 250],  // violet — education
  university:    [196, 181, 253],  // light violet
  power:         [245, 158,  11],  // amber — EGAT / PEA infrastructure
  transit:       [ 96, 165, 250],  // blue-400 — SRT / bus stations
  tall:          [125, 211, 252],  // sky-300 — skyline height marker
};

// Height ramp for buildings that don't fit a named category — imported from ../lib/building.

/**
 * Render every municipality building as a filled, tappable 3D box.
 *
 * 2D: hairline footprints — landmarks warmer, ordinary buildings dim.
 * 3D: extruded to real height. Landmarks get their category colour at full
 *     vibrancy regardless of height. Ordinary buildings stay on the blue
 *     height-ramp (sky-300 → sky-700). The result: the mayor can read the
 *     urban topology — the gold temple cluster, amber hotel strip, red
 *     hospital district — all visible in a single 3D view.
 * 3DS: ghosted for the substructure (utilities) cutaway view.
 */
export function buildingsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, BuildingProperties>,
  options: { extruded?: boolean; ghosted?: boolean } = {},
) {
  const extruded = options.extruded ?? false;
  const ghosted  = options.ghosted  ?? false;
  const lineA = ghosted ? 110 : 220;
  // There is deliberately no `fillA` constant here. It existed alongside a
  // hardcoded 32 inside getFillColor's own ghosted branch, so the alpha had
  // two homes and only one was read — the classic shape of a value that
  // silently stops tracking the other. The fill alpha lives with the branch
  // that applies it.

  return new GeoJsonLayer({
    id: "municipality-buildings",
    data: collection as unknown as FeatureCollection,
    // Source GeoJSON is already valid (single FeatureCollection with proper
    // geometry); skipping normalization saves a full pass over 20k+ features
    // every time the layer instance is created.
    _normalize: false,
    // Stroke is a full second draw pass over 20k+ polygons. In extruded (3D) mode
    // the lighting already provides depth cues, so we skip the edge pass entirely.
    // In flat 2D mode we keep it — edges are the only way to distinguish footprints.
    stroked: !extruded,
    filled: true,
    pickable: true,
    autoHighlight: false,
    extruded,
    elevationScale: extruded && !ghosted ? 1.65 : 1,
    material: extruded && !ghosted
      ? { ambient: 0.72, diffuse: 0.82, shininess: 24, specularColor: [255, 245, 220] }
      : false,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) => {
      const kind = classifyBuilding(f.properties);
      if (ghosted) {
        const base = kind ? LANDMARK_COLOR[kind] : heightColor(buildingHeightMeters(f.properties));
        return [base[0], base[1], base[2], 32] as [number, number, number, number];
      }
      if (extruded) {
        if (kind) {
          const c = LANDMARK_COLOR[kind];
          return [c[0], c[1], c[2], 230] as [number, number, number, number];
        }
        const c = heightColor(buildingHeightMeters(f.properties));
        return [c[0], c[1], c[2], 218] as [number, number, number, number];
      }
      // 2D flat view — landmarks stand out, ordinary buildings are dim
      if (kind) {
        const c = LANDMARK_COLOR[kind];
        return [c[0], c[1], c[2], 130] as [number, number, number, number];
      }
      return f.properties.name
        ? [14, 165, 233, 100] as [number, number, number, number]
        : [80, 110, 140, 60]  as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineColor: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) => {
      const kind = classifyBuilding(f.properties);
      if (kind) {
        const c = LANDMARK_COLOR[kind];
        return [c[0], c[1], c[2], lineA] as [number, number, number, number];
      }
      return f.properties.name
        ? [14, 165, 233, lineA] as [number, number, number, number]
        : [15, 23, 42, lineA] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) =>
      classifyBuilding(f.properties) ? 1.2 : 0.6) as unknown as number,
    lineWidthMinPixels: extruded && !ghosted ? 0.7 : 0.5,
    getElevation: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) =>
      buildingHeightMeters(f.properties)) as unknown as number,
    opacity: ghosted ? 0.35 : 1,
    updateTriggers: {
      getFillColor: [extruded, ghosted],
      getLineColor: [ghosted],
      getElevation: [extruded, ghosted],
    },
  });
}

/**
 * Air4Thai PCD stations — official Thai government air-quality monitors inside
 * Nakhon Ratchasima. Coloured by PM2.5 (US-EPA bands), labelled with the live
 * AQI so an operator sees the real sensor readings on the map. Pickable so the
 * tooltip shows station name + readings.
 */
export function air4thaiLayer(stations: AirQualityPoint[]) {
  return new ScatterplotLayer<AirQualityPoint>({
    id: "air4thai-stations",
    data: stations,
    getPosition: (s) => [s.lng, s.lat],
    getRadius: 90,
    radiusMinPixels: 9,
    radiusMaxPixels: 20,
    getFillColor: (s) => {
      const v = s.pm25 ?? -1;
      if (v < 0) return [148, 163, 184, 200]; // no reading — slate
      if (v <= 12) return [34, 197, 94, 255];
      if (v <= 35.4) return [250, 204, 21, 255];
      if (v <= 55.4) return [249, 115, 22, 255];
      if (v <= 150.4) return [239, 68, 68, 255];
      return [127, 29, 29, 255];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 2,
    pickable: true,
  });
}

export function cctvLayer(cameras: CctvCamera[]) {
  return new IconLayer<CctvCamera>({
    id: "cctv-cameras",
    data: cameras,
    getPosition: (c: CctvCamera) => [c.lng, c.lat],
    // Camera pictogram (see map/cameraIcon.ts) — reads as a camera at every
    // zoom, unlike the old status dots. Tint carries the online state.
    iconAtlas: cameraIconAtlas(),
    iconMapping: CAMERA_ICON_MAPPING,
    getIcon: () => "cam",
    getSize: 30,
    sizeMinPixels: 24,
    sizeMaxPixels: 46,
    // Up = green, Down = red, unknown = neutral grey. Camera markers without
    // a reported online state (e.g. Longdo) fall back to the neutral grey so
    // we don't lie about a vendor that doesn't publish health.
    getColor: (c: CctvCamera) => {
      if (c.online === true) return [16, 185, 129, 255]; // green
      if (c.online === false) return [239, 68, 68, 255]; // red
      return [229, 231, 235, 255];                        // neutral grey (Longdo default)
    },
    pickable: true,
    // The hover tooltip is rendered by DeckGL's getTooltip at the
    // component level (tooltipForPickMemo in App.tsx), not per-layer.
    // This keeps tooltip styling consistent across all clickable
    // features and lets us upgrade the markup in one place.
  });
}

// IconLayer reference, kept so 3D-extruded buildings + vehicle icons can be added later.
export { IconLayer };

// ─── Surrounding buildings (urban fabric ~1km around campus) ─────────────
export interface SurroundingBuildingProperties {
  id: string;
  name: string | null;
  nameEn: string | null;
  nameTh: string | null;
  building: string;
  levels: number | null;
  height: number | null;
  operator: string | null;
}

function surroundingBuildingHeightMeters(props: SurroundingBuildingProperties): number {
  if (props.height) return props.height;
  if (props.levels) return props.levels * 3.2;
  return 12;
}

export function surroundingBuildingsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, SurroundingBuildingProperties>,
  options: { extruded?: boolean; ghosted?: boolean } = {},
) {
  const extruded = options.extruded ?? false;
  const ghosted = options.ghosted ?? false;
  const fillAlpha = ghosted ? 28 : 0.85;
  const lineAlpha = ghosted ? 90 : 180;

  return new GeoJsonLayer({
    id: "surrounding-buildings",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    extruded,
    material: extruded && !ghosted
      ? { ambient: 0.5, diffuse: 0.6, shininess: 8, specularColor: [200, 200, 220] }
      : false,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, SurroundingBuildingProperties>) => {
      const h = surroundingBuildingHeightMeters(f.properties);
      // Cooler palette than campus buildings so campus reads as the warm focus
      if (h >= 100) return [160, 140, 200, ghosted ? fillAlpha : 200];
      if (h >= 60) return [120, 130, 180, ghosted ? fillAlpha : 185];
      if (h >= 35) return [90, 110, 150, ghosted ? fillAlpha : 170];
      return [70, 90, 120, ghosted ? fillAlpha : 155];
    }) as unknown as [number, number, number, number],
    getLineColor: ((f: Feature<Polygon | MultiPolygon, SurroundingBuildingProperties>) =>
      f.properties.name
        ? ([140, 160, 200, lineAlpha] as [number, number, number, number])
        : ([90, 110, 140, lineAlpha] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: 0.6,
    lineWidthMinPixels: 0.5,
    getElevation: ((f: Feature<Polygon | MultiPolygon, SurroundingBuildingProperties>) =>
      surroundingBuildingHeightMeters(f.properties)) as unknown as number,
    opacity: ghosted ? 0.3 : 0.9,
    updateTriggers: {
      getFillColor: [extruded, ghosted],
      getLineColor: [ghosted],
    },
  });
}

// ─── Bangkok district boundaries ─────────────────────────────────────────
export interface DistrictProperties {
  id: string;
  nameTh: string;
  nameEn: string;
  code: string;
  areaKm2: number;
}

export function districtBoundariesLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, DistrictProperties>,
) {
  return new GeoJsonLayer({
    id: "bangkok-districts",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    getLineColor: [255, 255, 255, 160],
    getLineWidth: 2,
    lineWidthMinPixels: 1.5,
    lineWidthMaxPixels: 3,
  });
}

export function districtLabelsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, DistrictProperties>,
) {
  // Compute centroids for labels
  const labels = collection.features.map((f) => {
    let cx = 0, cy = 0, n = 0;
    const coords = f.geometry.type === "Polygon"
      ? f.geometry.coordinates[0]
      : f.geometry.coordinates[0][0];
    for (const [x, y] of coords) { cx += x; cy += y; n++; }
    return {
      position: [cx / n, cy / n, 0] as [number, number, number],
      text: `${f.properties.nameEn}`,
      sub: f.properties.nameTh,
    };
  });
  return new TextLayer({
    id: "sikhio-district-labels",
    data: labels,
    getPosition: (d) => d.position,
    getText: (d) => d.text,
    getSize: 16,
    getColor: [255, 255, 255, 200],
    getAngle: 0,
    getTextAnchor: "middle",
    getAlignmentBaseline: "center",
    billboard: true,
    fontFamily: "'IBM Plex Sans Condensed', sans-serif",
    fontWeight: "bold",
    getBackgroundColor: [0, 0, 0, 120],
    background: true,
    parameters: { depthTest: false },
    pickable: false,
  });
}

// ─── Flood-prone areas ───────────────────────────────────────────────────
export interface FloodAreaProperties {
  id: string;
  nameTh: string;
  nameEn: string;
  risk: "low" | "medium" | "high";
  cause: string;
  frequency: string;
  lastMajor: string;
}

const FLOOD_COLORS: Record<FloodAreaProperties["risk"], [number, number, number, number]> = {
  low:    [250, 204, 21, 70],
  medium: [245, 158, 11, 90],
  high:   [239, 68, 68, 110],
};

export function floodProneAreasLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, FloodAreaProperties>,
) {
  return new GeoJsonLayer({
    id: "flood-prone-areas",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, FloodAreaProperties>) =>
      FLOOD_COLORS[f.properties.risk] ?? [200, 200, 200, 60]) as unknown as [number, number, number, number],
    getLineColor: ((f: Feature<Polygon | MultiPolygon, FloodAreaProperties>) => {
      const c = FLOOD_COLORS[f.properties.risk] ?? [200, 200, 200, 60];
      return [c[0], c[1], c[2], 200] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 1.5,
    lineWidthMinPixels: 1,
    getLineDashArray: [4, 3],
    lineDashJustified: true,
  });
}

/** Pulsing dot at the operator's GPS fix. Two concentric circles + an
 *  accuracy disk underneath. */
export function devicePresenceLayer(
  lng: number,
  lat: number,
  accuracyM: number | null,
) {
  const data = [{ lng, lat, accuracyM }];
  // Convert accuracy metres to a rough pixel radius at zoom 16 (campus zoom).
  // deck.gl Scatterplot uses `radius` in meters when `radiusUnits === "meters"`
  // (the default for this layer). We pass the actual metre value.
  return [
    new ScatterplotLayer({
      id: "device-accuracy",
      data,
      getPosition: (d) => [d.lng, d.lat] as [number, number],
      getRadius: () => accuracyM ?? 30,
      radiusUnits: "meters",
      radiusMinPixels: 8,
      radiusMaxPixels: 240,
      getFillColor: [56, 189, 248, 50],
      stroked: false,
      pickable: false,
    }),
    new ScatterplotLayer({
      id: "device-dot",
      data,
      getPosition: (d) => [d.lng, d.lat] as [number, number],
      getRadius: 16,
      radiusUnits: "pixels",
      getFillColor: [56, 189, 248, 240],
      stroked: true,
      getLineColor: [255, 255, 255, 240],
      lineWidthUnits: "pixels",
      getLineWidth: 2,
      pickable: false,
    }),
  ];
}

/** NASA GIBS WMTS tile layer — free, no API key. */
/**
 * NASA GIBS global tile layer (MODIS true-color, NDVI, LST, flood).
 * Always visible when called — the caller decides whether to add it to
 * the deck.gl layer list based on enabledLayers. The zoom restriction was
 * removed: if the user explicitly turns a satellite layer on, they should
 * see it. Layer descriptions in the palette already say "regional zoom".
 */
// GIBS product → (format, max-level) heuristic. RGB / true-color products
// ship as JPG at Level 9; thematic palettes (NDVI, LST, AOD, NO2, IMERG)
// ship as PNG at lower max levels.
function inferFormat(productId: string): "jpg" | "png" {
  if (productId.includes("CorrectedReflectance")) return "jpg";
  if (productId.includes("TrueColor")) return "jpg";
  if (productId.includes("DayNightBand")) return "png";
  return "png";
}
function inferLevel(productId: string): 6 | 7 | 8 | 9 {
  if (productId.includes("IMERG")) return 6;
  if (productId.includes("OMI_") || productId.includes("AOD")) return 6;
  if (productId.includes("Land_Surface_Temp")) return 7;
  if (productId.includes("DayNightBand")) return 8;
  return 9;
}

interface GibsOpts {
  /** "jpg" for true-color / RGB products, "png" for thematic / index products. */
  format?: "jpg" | "png";
  /** Tile matrix max level: 9 for high-res, 7-8 for thematic, 6 for IMERG / OMI. */
  level?: 6 | 7 | 8 | 9;
}
export function gibsLayer(
  productId: string,
  date?: string,
  opacity = 0.6,
  opts: GibsOpts = {},
) {
  const dateStr = date ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const format = opts.format ?? inferFormat(productId);
  const level = opts.level ?? inferLevel(productId);
  return new TileLayer({
    id: `gibs-${productId}`,
    maxRequests: RASTER_MAX_REQUESTS,
    data:
      `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${productId}/default/${dateStr}` +
      `/GoogleMapsCompatible_Level${level}/{z}/{y}/{x}.${format}`,
    minZoom: 0,
    maxZoom: level,
    tileSize: 256,
    opacity,
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as string,
        bounds: [w, s, e, n],
      });
    },
  });
}

/**
 * How many raster tiles may be in flight at once.
 *
 * deck.gl's TileLayer defaults to `maxRequests: 6`, which is fine for a
 * layer that only ever shows one tile — and catastrophic for a basemap.
 * The EXEC lens viewport needs 16 tiles at 1440px and more than a hundred
 * on a wide monitor, and the layer selects all of them correctly; it just
 * refuses to fetch more than six at a time.
 *
 * The visible symptom was the worst kind: the six that did load painted as
 * a hard-edged rectangle of satellite imagery in the middle of the map,
 * over the light vector basemap, with the rest of the viewport bare. It
 * read as a rendering bug rather than as a throttled download.
 *
 * 64 covers the widest realistic viewport at the zoom range this dashboard
 * allows (the controller caps at z20) without opening a stampede of
 * requests against a public tile server.
 */
const RASTER_MAX_REQUESTS = 64;

/** Esri World Imagery — high-resolution satellite, publicly accessible. */
export function esriSatelliteLayer(opacity = 0.9) {
  return new TileLayer({
    id: "satellite-esri",
    data: "https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,
    maxRequests: RASTER_MAX_REQUESTS,
    opacity,
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as string,
        bounds: [w, s, e, n],
      });
    },
  });
}

/** OpenTopoMap terrain — for elevation/contour context. Lower max zoom (17). */
export function openTopoTerrainLayer(opacity = 0.6) {
  return new TileLayer({
    id: "satellite-terrain",
    maxRequests: RASTER_MAX_REQUESTS,
    data: [
      "https://a.tile.opentopomap.org/{z}/{x}/{y}.png",
      "https://b.tile.opentopomap.org/{z}/{x}/{y}.png",
      "https://c.tile.opentopomap.org/{z}/{x}/{y}.png",
    ],
    minZoom: 0,
    maxZoom: 17,
    tileSize: 256,
    opacity,
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as string,
        bounds: [w, s, e, n],
      });
    },
  });
}

/**
 * Himawari-9 Band 13 (clean infrared) via NASA GIBS WMS. Geostationary cloud
 * loop, 10-min cadence — best for spotting incoming storms over Bangkok.
 * Uses WMS (not WMTS) because GIBS only exposes Himawari that way.
 */
export function himawariInfraredLayer(opacity = 0.55) {
  const today = new Date().toISOString().slice(0, 10);
  // GIBS WMS template — single tile per request, but TileLayer drives the bbox.
  const wmsBase =
    "https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi?" +
    "SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=TRUE&" +
    `LAYERS=Himawari_AHI_Band13_Clean_Infrared&TIME=${today}&CRS=EPSG:3857&WIDTH=256&HEIGHT=256`;
  return new TileLayer({
    id: "satellite-himawari",
    maxRequests: RASTER_MAX_REQUESTS,
    minZoom: 0,
    maxZoom: 9,
    tileSize: 256,
    opacity,
    getTileData: async ({ bbox }) => {
      // bbox in Web Mercator metres. GIBS expects BBOX=minx,miny,maxx,maxy.
      const { west, south, east, north } = bbox as { west: number; south: number; east: number; north: number };
      // Convert lat/lng to mercator metres (deck.gl gives lng/lat).
      const R = 6378137;
      const toMx = (lng: number) => (lng * Math.PI * R) / 180;
      const toMy = (lat: number) => Math.log(Math.tan((Math.PI * (90 + lat)) / 360)) * R;
      const url = `${wmsBase}&BBOX=${toMx(west)},${toMy(south)},${toMx(east)},${toMy(north)}`;
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        if (typeof createImageBitmap === "function") {
          return await createImageBitmap(blob);
        }
        // Old Safari without createImageBitmap — skip tile rather than leak blob URLs
        return null;
      } catch {
        return null;
      }
    },
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as ImageBitmap,
        bounds: [w, s, e, n],
      });
    },
  });
}

// ─── Road network — classified by priority ──────────────────────────────
// Renders the OSM road network as a visible map element. Width + colour
// scale by `priority`: motorway/primary = thick warm, secondary = medium
// cyan, tertiary = thin cyan, residential/lane = thinnest neutral.

const ROAD_STYLE: Record<number, { color: [number, number, number]; width: number }> = {
  6: { color: [251, 113, 133], width: 4.5 },  // motorway
  5: { color: [251, 146, 60],  width: 3.5 },  // primary / secondary
  4: { color: [125, 211, 252], width: 2.5 },  // tertiary
  3: { color: [148, 163, 184], width: 1.5 },  // residential / lane
  2: { color: [148, 163, 184], width: 1.0 },  // unclassified / minor
};

export function roadNetworkLayer(collection: FeatureCollection<LineString, ClassifiedRoadProps>) {
  return new GeoJsonLayer({
    id: "road-network",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    getLineColor: ((f: Feature<LineString, ClassifiedRoadProps>) => {
      const s = ROAD_STYLE[f.properties.priority] ?? ROAD_STYLE[3];
      return [s.color[0], s.color[1], s.color[2], 180] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, ClassifiedRoadProps>) => {
      const s = ROAD_STYLE[f.properties.priority] ?? ROAD_STYLE[3];
      return s.width;
    }) as unknown as number,
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 0.5,
    lineWidthMaxPixels: 6,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// OPEN DATA POINTS (data.go.th)
// ═══════════════════════════════════════════════════════════════════════

export interface DatagoPoint {
  id: string;
  name: string;
  nameEn?: string;
  category: string;
  lat: number;
  lng: number;
  source: string;
  attribution?: string;
}

// data.go.th points — government POI markers
const DATAGO_COLOR: Record<string, [number, number, number]> = {
  school:    [167, 139, 250],
  hospital:  [239, 68, 68],
  health:    [251, 113, 133],
  temple:    [251, 191, 36],
  market:    [16, 185, 129],
  office:    [56, 189, 248],
  default:   [192, 132, 252],
};

export function datagoPointsLayer(points: DatagoPoint[]) {
  return new ScatterplotLayer<DatagoPoint>({
    id: "datago-points",
    data: points,
    pickable: true,
    radiusUnits: "pixels",
    getPosition: (p) => [p.lng, p.lat],
    getRadius: 5,
    getFillColor: (p) => {
      const cat = p.category.toLowerCase();
      for (const k of Object.keys(DATAGO_COLOR)) if (cat.includes(k)) return [...DATAGO_COLOR[k], 220] as [number, number, number, number];
      return [...DATAGO_COLOR.default, 200] as [number, number, number, number];
    },
    getLineColor: [255, 255, 255, 180],
    stroked: true,
    lineWidthMinPixels: 1,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// DISTANCE GRID (1 / 5 / 10 km rings from municipality centroid)
// Useful for: response-time radii, evacuation planning, ferry reach, AIS
// proximity, mayor's "I can be there in X minutes" framing.
// ═══════════════════════════════════════════════════════════════════════

import { PolygonLayer } from "@deck.gl/layers";

const KM_RING_COLORS: Record<number, [number, number, number, number]> = {
  1:  [14, 165, 233, 220],  // cerulean
  5:  [56, 189, 248, 180],  // sky-400
  10: [125, 211, 252, 140], // sky-300
};

/** Build a ring polygon at `radiusKm` from [lng, lat]. 64-segment circle. */
function ringAt(lng: number, lat: number, radiusKm: number): [number, number][] {
  const segs = 96;
  const earthR = 6371;
  const out: [number, number][] = [];
  for (let i = 0; i <= segs; i++) {
    const brg = (i * 2 * Math.PI) / segs;
    const dr = radiusKm / earthR;
    const lat1 = (lat * Math.PI) / 180;
    const lng1 = (lng * Math.PI) / 180;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(brg));
    const lng2 = lng1 + Math.atan2(
      Math.sin(brg) * Math.sin(dr) * Math.cos(lat1),
      Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2),
    );
    out.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  return out;
}

export function distanceGridLayer(
  center: [number, number],
  radiiKm: number[] = [1, 5, 10],
) {
  const rings = radiiKm.map((km) => ({
    km,
    contour: ringAt(center[0], center[1], km),
  }));
  return new PolygonLayer<{ km: number; contour: [number, number][] }>({
    id: "distance-grid",
    data: rings,
    pickable: false,
    filled: false,
    stroked: true,
    getPolygon: (d) => d.contour,
    getLineColor: (d) => KM_RING_COLORS[d.km] ?? [148, 163, 184, 160],
    getLineWidth: (d) => (d.km === 10 ? 2.5 : d.km === 5 ? 2 : 1.5),
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 1,
  });
}

// Text labels for each ring
export function distanceGridLabelsLayer(
  center: [number, number],
  radiiKm: number[] = [1, 5, 10],
) {
  // Place each label slightly north-east of the centroid at radius
  const labels = radiiKm.map((km) => {
    const earthR = 6371;
    const dr = km / earthR;
    const brg = Math.PI / 4; // 45° NE
    const lat1 = (center[1] * Math.PI) / 180;
    const lng1 = (center[0] * Math.PI) / 180;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(brg));
    const lng2 = lng1 + Math.atan2(
      Math.sin(brg) * Math.sin(dr) * Math.cos(lat1),
      Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2),
    );
    return { km, position: [(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI] as [number, number] };
  });
  return new TextLayer<{ km: number; position: [number, number] }>({
    id: "distance-grid-labels",
    data: labels,
    pickable: false,
    getPosition: (d) => d.position,
    getText: (d) => `${d.km} km`,
    getSize: 11,
    sizeUnits: "pixels",
    getColor: (d) => KM_RING_COLORS[d.km] ?? [200, 220, 240, 220],
    fontFamily: "IBM Plex Mono, monospace",
    fontWeight: 600,
    getTextAnchor: "start",
    getAlignmentBaseline: "bottom",
    background: true,
    backgroundPadding: [4, 2],
    getBackgroundColor: [3, 16, 31, 220],
  });
}

// ═══════════════════════════════════════════════════════════════════════
// CIVIC + WATERWAYS — consistent category palette + hover tooltips
// ═══════════════════════════════════════════════════════════════════════

export interface CivicPoint {
  kind: CivicKind;
  name?: string;
  nameTh?: string;
  nameEn?: string;
}
export type CivicKind =
  | "hospital" | "clinic" | "pharmacy"
  | "school" | "university" | "kindergarten"
  | "police" | "fire"
  | "government" | "courthouse" | "post"
  | "temple-buddhist" | "church" | "mosque"
  | "market" | "bus-station" | "ferry"
  | "power-substation" | "water-works" | "wastewater"
  | "other";

// Shared category palette — coded by colour family so the legend is
// learnable across the whole dashboard. Health = red family; Education
// = violet; Safety = amber/orange; Government = cerulean; Religion =
// gold; Utility = teal; Transport = sky.
export const CIVIC_PALETTE: Record<CivicKind, { color: [number, number, number]; glyph: string; label: string }> = {
  hospital:         { color: [239, 68, 68],  glyph: "✚", label: "Hospital" },
  clinic:           { color: [251, 113, 133], glyph: "✚", label: "Clinic" },
  pharmacy:         { color: [253, 164, 175], glyph: "Rx", label: "Pharmacy" },
  school:           { color: [167, 139, 250], glyph: "🅢", label: "School" },
  university:       { color: [196, 181, 253], glyph: "Ⓤ", label: "University" },
  kindergarten:     { color: [221, 214, 254], glyph: "Ⓚ", label: "Kindergarten" },
  police:           { color: [56, 189, 248],  glyph: "P",  label: "Police" },
  fire:             { color: [251, 146, 60],  glyph: "🜂", label: "Fire station" },
  government:       { color: [14, 165, 233],  glyph: "⌬", label: "Government" },
  courthouse:       { color: [3, 105, 161],   glyph: "⚖", label: "Courthouse" },
  post:             { color: [125, 211, 252], glyph: "✉", label: "Post office" },
  "temple-buddhist":{ color: [251, 191, 36],  glyph: "卐", label: "Temple" },
  church:           { color: [253, 224, 71],  glyph: "✟", label: "Church" },
  mosque:           { color: [250, 204, 21],  glyph: "☪", label: "Mosque" },
  market:           { color: [16, 185, 129],  glyph: "▦", label: "Market" },
  "bus-station":    { color: [125, 211, 252], glyph: "🚌", label: "Bus station" },
  ferry:            { color: [251, 191, 36],  glyph: "⛴", label: "Ferry pier" },
  "power-substation": { color: [245, 158, 11], glyph: "⚡", label: "Substation" },
  "water-works":    { color: [34, 211, 238],  glyph: "💧", label: "Water works" },
  wastewater:       { color: [13, 148, 136],  glyph: "♻", label: "Wastewater" },
  other:            { color: [148, 163, 184], glyph: "○",  label: "Other" },
};

function readKind(props: Record<string, unknown> | null | undefined): CivicKind {
  const k = (props?.kind as string) ?? "other";
  return (k in CIVIC_PALETTE ? (k as CivicKind) : "other");
}

export function civicPointsLayer(collection: FeatureCollection<Point, Record<string, unknown>>) {
  return new ScatterplotLayer<Feature<Point, Record<string, unknown>>>({
    id: "civic-points",
    data: collection.features,
    pickable: true,
    radiusUnits: "pixels",
    getPosition: (f) => f.geometry.coordinates as [number, number],
    getRadius: (f) => {
      const k = readKind(f.properties);
      // High-importance kinds get bigger dots so the mayor sees them first
      if (k === "hospital" || k === "fire" || k === "police" || k === "government") return 6;
      if (k === "university" || k === "courthouse") return 5;
      return 4;
    },
    getFillColor: (f) => {
      const c = CIVIC_PALETTE[readKind(f.properties)].color;
      return [...c, 220] as [number, number, number, number];
    },
    getLineColor: [255, 255, 255, 220],
    stroked: true,
    lineWidthMinPixels: 1,
  });
}

export function waterwaysLayer(collection: FeatureCollection<LineString, Record<string, unknown>>) {
  // Static base line — keeps the existing dashboard look intact.
  const base = new GeoJsonLayer({
    id: "waterways",
    data: collection,
    pickable: true,
    stroked: true,
    filled: false,
    getLineColor: (f) => {
      const t = String(f.properties?.waterway ?? "stream").toLowerCase();
      return (WATERWAY_COLOR[t] ?? WATERWAY_COLOR.stream);
    },
    getLineWidth: (f) => {
      const t = String(f.properties?.waterway ?? "stream").toLowerCase();
      return t === "river" ? 4 : t === "canal" ? 2.5 : 1;
    },
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 1,
  });
  return base;
}

/** Direction arrows on rivers — "▾" glyphs placed at intervals along
 *  each river's line, pointing along the line's tangent. Reads as
 *  "this is which way the water is moving" at a glance.
 *
 *  Why static glyphs over animated dashes:
 *   - Static works at any zoom, in any map style, with no rAF tax.
 *   - The "God Mode" view is for the Mayor and the war room — clarity
 *     beats motion. The user can read a static arrow in 200 ms.
 *   - deck.gl's animated `getDashArray` needs a custom uniform tick
 *     (re-render at 60 fps for a moving dash) which costs more than
 *     this small layer for the same legibility.
 *
 *  `waterFlowArrowsLayer` is exported separately so App.tsx can add it
 *  only when the AGRI/ENV/EAR/SAF/INT lens is on, where the water
 *  story is the headline. */
export function waterFlowArrowsLayer(
  collection: FeatureCollection<LineString, Record<string, unknown>>,
) {
  const arrows = buildFlowArrows(collection);
  // TextLayer with a "▾" glyph rendered in a contrasting colour.
  // We can't ship a custom icon font without bundling, so the chevron
  // is the cleanest option that survives zoom + theme switches.
  //
  // ROTATION — `angle` is a compass bearing (0° = north, +90° = east), but
  // the "▾" glyph points DOWN at rotation 0 and deck.gl rotates clockwise.
  // `glyphRotationDeg` bridges the two. It used to be `angle - 90`, which
  // rendered every arrow rotated 90° clockwise from the truth: a
  // north-flowing river pointed east, an east-flowing one pointed south.
  // It was never caught because the only test of this geometry re-implemented
  // haversineM locally and could not see this line at all.
  return new TextLayer<WaterFlowArrow>({
    id: "water-flow-arrows",
    data: arrows,
    pickable: false,
    getPosition: (d) => d.position,
    getText: () => "▾",
    getSize: (d) => d.size,
    getColor: (d) => d.color,
    getAngle: (d) => glyphRotationDeg(d.angle),
    sizeUnits: "pixels",
    fontFamily: '"Source Sans 3", "IBM Plex Sans Thai", "Helvetica Neue", Arial, sans-serif',
    fontWeight: 600,
    billboard: false,
    background: false,
    // Black outline so the arrow stays readable on light + dark basemaps
    outlineWidth: 0,
  });
}

/** Lakes, ponds, reservoirs — filled polygons (ESRI Geo CDP water layer). */
export function waterBodiesLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>,
) {
  return new GeoJsonLayer({
    id: "water-bodies",
    data: collection as unknown as FeatureCollection,
    pickable: true,
    stroked: true,
    filled: true,
    getFillColor: [14, 165, 233, 90],
    getLineColor: [56, 189, 248, 200],
    lineWidthMinPixels: 1,
  });
}

/** SRT Northeastern Line — distinct from the road network. */
export function railwayLayer(collection: FeatureCollection<LineString, Record<string, unknown>>) {
  return new GeoJsonLayer({
    id: "railway",
    data: collection,
    pickable: true,
    stroked: true,
    filled: false,
    getLineColor: [226, 232, 240, 230],
    getLineWidth: 3,
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 2,
    // Dashed look via multipass isn't free in deck.gl GeoJsonLayer —
    // a bright rail colour + thicker stroke is enough to separate from roads.
  });
}

// ═══════════════════════════════════════════════════════════════════════
// FLOOD RISK
// Hand-authored polygons. Update as municipal GIS supplies real shapes.
// ═══════════════════════════════════════════════════════════════════════

// Fill alpha is deliberately low. These zones cover the whole town, so at the
// old 90–130 alpha they painted the basemap out entirely — the map read as one
// orange blob and you could no longer see the streets the risk applies to.
// The zone is communicated by its EDGE (see getLineColor below); the fill is
// only a hint that the edge encloses something. Same idiom as the rest of the
// map: draw the boundary, don't flood the surface.
const FLOOD_BASE: Record<string, [number, number, number, number]> = {
  high:   [239, 68, 68, 46],
  medium: [251, 146, 60, 38],
  low:    [251, 191, 36, 30],
};

/** Map a feature's `risk_level` (the actual property in
 *  sikhio-flood-risk.geojson) to a base RGBA. Older drafts used
 *  `severity`; the prior layer silently fell through to "medium"
 *  for every zone. Both keys are accepted so re-runs of the source
 *  data don't break the map. */
function floodBaseColor(f: { properties?: Record<string, unknown> }): [number, number, number, number] {
  const sev = String(
    f.properties?.risk_level ??
    f.properties?.severity ??
    "medium",
  ).toLowerCase();
  return FLOOD_BASE[sev] ?? FLOOD_BASE.medium;
}

/** Lam Takhong's "normal high" storage in MCM — RID's published figure.
 *  When current storage sits at or above normal, downstream flood risk
 *  escalates; we tint the flood zones redder + more opaque so the
 *  dashboard's "God Mode" shows the situation waking up. Below 60% the
 *  zones show their historical severity; the user can tell the
 *  difference at a glance. */
function waterStressLevel(pctNormal: number | null | undefined): "calm" | "watch" | "elevated" | "high" {
  if (pctNormal == null) return "calm";
  if (pctNormal >= 100) return "high";
  if (pctNormal >= 90)  return "elevated";
  if (pctNormal >= 75)  return "watch";
  return "calm";
}

const STRESS_TINT: Record<"calm" | "watch" | "elevated" | "high", number> = {
  calm:     0,
  watch:    0.10,
  elevated: 0.25,
  high:     0.45,
};

function tintFloodColor(
  base: [number, number, number, number],
  stress: "calm" | "watch" | "elevated" | "high",
): [number, number, number, number] {
  if (stress === "calm") return base;
  const shift = STRESS_TINT[stress];
  // shift toward red (239,68,68) as stress rises
  const r = Math.round(base[0] + (239 - base[0]) * shift);
  const g = Math.round(base[1] + (68  - base[1]) * shift);
  const b = Math.round(base[2] + (68  - base[2]) * shift);
  // Ramp alpha with stress so the zone wakes up — but cap it low. Even at
  // "high" this must stay a tint over a legible basemap, never a wash.
  const a = Math.min(90, Math.round(base[3] + shift * 70));
  return [r, g, b, a];
}

export function floodRiskLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>,
  opts?: { damPctNormal?: number | null },
) {
  const stress = waterStressLevel(opts?.damPctNormal ?? null);
  return new GeoJsonLayer({
    id: "flood-risk",
    data: collection,
    pickable: true,
    stroked: true,
    filled: true,
    getFillColor: (f) => tintFloodColor(floodBaseColor(f), stress),
    getLineColor: (f) => {
      const base = floodBaseColor(f);
      const c = tintFloodColor(base, stress);
      // The edge carries the information now that the fill is a hint, so it is
      // near-opaque regardless of how faint the fill is.
      return [c[0], c[1], c[2], Math.min(255, c[3] + 185)];
    },
    getLineWidth: stress === "high" ? 2.5 : 1.5,
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 1,
    updateTriggers: {
      getFillColor: [stress],
      getLineColor: [stress],
      getLineWidth: [stress],
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// HERITAGE LAYERS — temples, old town, Chinese shrines
// ═══════════════════════════════════════════════════════════════════════

export interface HeritageFeatureProps {
  kind: "temple-spire" | "chinese-shrine" | "old-town-district";
  name: string;
  nameTh?: string;
  height?: number;
  era?: string;
  describe?: string;
}

/**
 * Temple spires — tall bright gold columns at known temple locations.
 * A real Thai temple's prang/chedi towers 15-50 m; this layer renders
 * it as a glowing gold column so it reads in the skyline from afar.
 * Deck.gl ScatterplotLayer with 3D emulation via thick radius + color.
 */
export function templeSpiresLayer(
  collection: FeatureCollection<Point, HeritageFeatureProps>,
) {
  const spires = collection.features.filter(
    (f) => f.properties.kind === "temple-spire" || f.properties.kind === "chinese-shrine",
  );

  // Base disk — wide amber foundation
  const base = new ScatterplotLayer<Feature<Point, HeritageFeatureProps>>({
    id: "temple-spires-base",
    data: spires,
    pickable: true,
    radiusUnits: "meters",
    getPosition: (f) => f.geometry.coordinates as [number, number],
    getRadius: (f) => f.properties.kind === "temple-spire" ? 14 : 8,
    getFillColor: [251, 191, 36, 200],
    getLineColor: [245, 158, 11, 255],
    stroked: true,
    lineWidthMinPixels: 1.5,
  });

  // Inner spire dot — bright, small, glowing
  const spire = new ScatterplotLayer<Feature<Point, HeritageFeatureProps>>({
    id: "temple-spires-tip",
    data: spires,
    pickable: false,
    radiusUnits: "pixels",
    getPosition: (f) => f.geometry.coordinates as [number, number],
    getRadius: (f) => f.properties.kind === "temple-spire" ? 5 : 3,
    getFillColor: [255, 255, 255, 240],
    getLineColor: [251, 191, 36, 255],
    stroked: true,
    lineWidthMinPixels: 1.5,
  });

  return [base, spire];
}

/**
 * Old town district boundary — hairline outline, warm amber fill at low
 * opacity so buildings inside remain visible but the district reads as
 * a distinct zone at all zoom levels.
 */
export function oldTownDistrictLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, HeritageFeatureProps>,
) {
  const districts = collection.features.filter(
    (f) => f.properties.kind === "old-town-district",
  );
  if (!districts.length) return null;
  return new GeoJsonLayer({
    id: "old-town-district",
    data: { type: "FeatureCollection", features: districts } as FeatureCollection,
    pickable: true,
    stroked: true,
    filled: true,
    getFillColor: [245, 158, 11, 18],   // very low opacity — just a haze
    getLineColor: [245, 158, 11, 200],
    getLineWidth: 2,
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 1.5,
  });
}

// ── NEWS PINS — geocoded headlines on the map ───────────────────────────
// When a news item mentions a known place (market, temple, hospital …),
// we pin it so the mayor can see "criminal activity at THIS market".

import type { IntelligenceItem } from "@sikhio/shared";

const NEWS_TAG_COLOR: Record<string, [number, number, number]> = {
  EM: [239, 68, 68],    // emergency — red
  PO: [251, 146, 60],   // police — orange
  FU: [167, 139, 250],  // funeral — violet
  IN: [56, 189, 248],   // infrastructure — sky
  BZ: [245, 158, 11],   // business — amber
  PU: [251, 113, 133],  // public health — pink
  FE: [250, 204, 21],   // festival — yellow
  HO: [52, 211, 153],   // honour — green
};

export function newsPinsLayer(items: IntelligenceItem[]) {
  const pinned = items.filter((it) => it.lat != null && it.lng != null);
  return new ScatterplotLayer<IntelligenceItem>({
    id: "news-pins",
    data: pinned,
    getPosition: (it) => [it.lng!, it.lat!],
    getRadius: (it) => {
      // Emergency / police items get bigger pins
      if (it.tags.includes("EM")) return 32;
      if (it.tags.includes("PO")) return 26;
      return 18;
    },
    radiusMinPixels: 5,
    radiusMaxPixels: 14,
    getFillColor: (it) => {
      const tag = it.tags.find((t) => t in NEWS_TAG_COLOR);
      const c = tag ? NEWS_TAG_COLOR[tag] : [148, 163, 184];
      return [c[0], c[1], c[2], 230] as [number, number, number, number];
    },
    stroked: true,
    getLineColor: [255, 255, 255, 240],
    lineWidthMinPixels: 2,
    pickable: true,
    // Subtle pulse effect via radius animation would need extra work;
    // for now the bright colour + white stroke makes them stand out.
  });
}

// ── Isochrone layer ───────────────────────────────────────────────────────────

const ISOCHRONE_MODE_COLOR: Record<string, [number, number, number, number]> = {
  walk:                   [59,  130, 246, 60],
  bicycle:                [16,  185, 129, 60],
  drive:                  [245, 158, 11,  60],
  approximated_transit:   [139, 92,  246, 60],
};

export function isochroneLayer(result: IsochroneResult | null) {
  if (!result) return null;
  const color = ISOCHRONE_MODE_COLOR[result.mode] ?? [59, 130, 246, 60];
  const [r, g, b] = color;
  // IsochroneResult.geometry is a structural Polygon/MultiPolygon union;
  // cast to the geojson package's Geometry so deck.gl's data prop accepts it.
  const geometry = result.geometry as unknown as Polygon | MultiPolygon;
  return new GeoJsonLayer({
    id: "isochrone-polygon",
    data: {
      type: "FeatureCollection" as const,
      features: [{ type: "Feature" as const, geometry, properties: {} }],
    },
    filled: true,
    stroked: true,
    getFillColor: [r, g, b, 55],
    getLineColor: [r, g, b, 200],
    getLineWidth: 2,
    lineWidthMinPixels: 1.5,
    pickable: false,
    updateTriggers: { getFillColor: [result.mode], getLineColor: [result.mode] },
  });
}

/** One picked row of the Lam Takhong water layer — dam or telemetry gauge. */
export type WaterMapPoint =
  | (BasinStation & { pointType: "station" })
  | {
      pointType: "dam";
      id: string;
      name: string;
      nameEn: string;
      lat: number;
      lng: number;
      pctNormal: number;
      storageMcm: number;
      alertLevel: DamReading["alertLevel"];
    };

/** Lam Takhong water layer — the dam plus HII gauges along the basin.
 *  Water-level gauges colored by HII situation level (1 critically-low …
 *  5 overflowing); rain gauges by 24 h accumulation; the dam by storage band. */
export function lamTakhongLayer(stations: BasinStation[], dam: DamReading | null) {
  const points: WaterMapPoint[] = [
    ...stations.map((s) => ({ ...s, pointType: "station" as const })),
    ...(dam
      ? [{
          pointType: "dam" as const,
          id: dam.id,
          name: dam.name,
          nameEn: dam.nameEn,
          lat: dam.lat,
          lng: dam.lng,
          pctNormal: dam.pctNormal,
          storageMcm: dam.storageMcm,
          alertLevel: dam.alertLevel,
        }]
      : []),
  ];
  return new ScatterplotLayer<WaterMapPoint>({
    id: "lam-takhong",
    data: points,
    getPosition: (p) => [p.lng, p.lat],
    getRadius: (p) => (p.pointType === "dam" ? 400 : 120),
    radiusMinPixels: 6,
    radiusMaxPixels: 26,
    getFillColor: (p) => {
      if (p.pointType === "dam") {
        // Storage band: low storage = drought amber/red, high = flood risk.
        if (p.alertLevel === "critical-low") return [220, 38, 38, 255];
        if (p.alertLevel === "low") return [249, 115, 22, 255];
        if (p.alertLevel === "watch-low") return [250, 204, 21, 255];
        if (p.alertLevel === "watch-high" || p.alertLevel === "high") return [59, 130, 246, 255];
        if (p.alertLevel === "critical-high") return [220, 38, 38, 255];
        return [34, 197, 94, 255];
      }
      if (p.kind === "rain") {
        const mm = p.rain24hMm ?? 0;
        if (mm >= 90) return [30, 64, 175, 255];
        if (mm >= 35) return [59, 130, 246, 255];
        if (mm >= 1) return [125, 211, 252, 255];
        return [148, 163, 184, 200]; // dry — slate
      }
      const lvl = p.situationLevel ?? 3;
      if (lvl >= 5) return [220, 38, 38, 255];
      if (lvl >= 4) return [249, 115, 22, 255];
      if (lvl <= 1) return [217, 119, 6, 255]; // critically low — drought amber
      if (lvl <= 2) return [250, 204, 21, 255];
      return [34, 197, 94, 255];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 2,
    pickable: true,
  });
}

/** Dam outflow indicator — a bright blue bar drawn ~1.5 km downstream
 *  from the dam whenever `releasedMcmDay > 0`. Reads as "the dam is
 *  releasing water" without needing a separate legend.
 *
 *  Renders the outflow at the dam's geographic position shifted
 *  ~1.5 km in the river's downstream direction (we project from the
 *  Lam Takhong River's first coordinate pair; OSM's order is
 *  upstream→downstream so the first pair is the source, the last
 *  is the river mouth).
 *
 *  The bar's width is a function of release volume so a small dry-season
 *  release shows as a thin line and a 50 MCM/day monsoon release shows
 *  as a thick pulse — instantly readable at any zoom. */
export function damOutflowLayer(
  dam: DamReading | null,
  rivers: FeatureCollection<LineString, Record<string, unknown>> | null,
) {
  if (!dam) return null;
  // The dam releases are published as MCM/day. Anything below
  // 0.05 MCM/day is "essentially closed" (rounding noise from upstream);
  // we don't draw a misleading sliver in that case.
  const release = dam.releasedMcmDay ?? 0;
  if (release <= 0.05) return null;
  // Find the Lam Takhong river to get its downstream tangent
  let downstream: [number, number] | null = null;
  if (rivers) {
    const lam = rivers.features.find(
      (f) => String(f.properties?.waterway ?? "").toLowerCase() === "river"
              && (String(f.properties?.name ?? "").includes("ตะคอง")
                  || String(f.properties?.name_en ?? "").toLowerCase().includes("takhong")),
    );
    if (lam) {
      const coords = lam.geometry.coordinates as [number, number][];
      if (coords.length >= 2) {
        // The last segment points downstream — use it as the direction
        const b = coords[coords.length - 1];
        downstream = b;
      }
    }
  }
  // If we can't find the river, project east from the dam (Lam Takhong
  // dam releases flow east into the Lam Takhong River). Fallback is
  // safe — the indicator still shows "the dam is releasing" even if
  // it's pointing the wrong way.
  const projLng = downstream ? downstream[0] : dam.lng + 0.012;
  const projLat = downstream ? downstream[1] : dam.lat - 0.002;
  // Bar width: 2 px (dry trickle) → 14 px (heavy release). Log scale
  // so the visual difference between 0.5 and 5 MCM/day is readable
  // but 30 vs 60 MCM/day doesn't blow out the canvas.
  const widthPx = Math.max(2, Math.min(14, 2 + Math.log10(release + 1) * 5));
  // Color: bright cyan-blue, transparent enough to layer over the river
  return new ScatterplotLayer<{ position: [number, number]; r: number }>({
    id: "dam-outflow",
    data: [{ position: [projLng, projLat], r: widthPx }],
    getPosition: (d) => d.position,
    getRadius: (d) => d.r,
    radiusUnits: "pixels",
    radiusMinPixels: 2,
    radiusMaxPixels: 14,
    getFillColor: [56, 189, 248, 240],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: false,
  });
}

/** Upstream → dam flow lines.
 *
 *  For each HII basin gauge within ~35 km, draw a thin line from the
 *  gauge to the dam. The line's color reflects the gauge's situation
 *  level (critically-low / normal / overflowing). The result is a
 *  "spider" radiating into the dam — instantly reads as "this is
 *  where the water is coming from" and the colours tell the operator
 *  which upstream sub-basin is contributing the most pressure.
 *
 *  Excluded from the map by default (no city block has "show upstream
 *  gauges" as a default); caller decides whether to push the layer.
 *  Stays decoupled from the lam-takhong gauge dots so the dots aren't
 *  obscured. */
export function upstreamToDamLayer(
  stations: BasinStation[],
  dam: DamReading | null,
) {
  if (!dam || stations.length === 0) return null;
  // Water-level gauges only — rain gauges don't contribute to the
  // upstream-to-dam flow story at this zoom.
  const wl = stations.filter((s) => s.kind === "waterlevel" && s.lat != null && s.lng != null);
  if (wl.length === 0) return null;
  const data = wl.map((s) => ({
    source: [s.lng as number, s.lat as number] as [number, number],
    target: [dam.lng, dam.lat] as [number, number],
    level: s.situationLevel ?? 3,
    name: s.name,
  }));
  return new PathLayer<typeof data[number]>({
    id: "upstream-to-dam",
    data,
    pickable: true,
    widthUnits: "pixels",
    getWidth: 1.5,
    getPath: (d) => [d.source, d.target],
    getColor: (d) => {
      // Match the lam-takhong gauge colour band so the line and the
      // dot at its end share semantics.
      const lvl = d.level;
      if (lvl >= 5) return [220, 38, 38, 180];
      if (lvl >= 4) return [249, 115, 22, 180];
      if (lvl <= 1) return [217, 119, 6, 180];
      if (lvl <= 2) return [250, 204, 21, 180];
      return [59, 130, 246, 140]; // normal — soft blue
    },
    // Lines are intentionally thin (1.5 px) so they read as a
    // "channel of influence" between gauge and dam, not as a physical
    // pipe. deck.gl's PathLayer in this version doesn't expose
    // getDashArray, so the dash effect ships as the subtle alpha +
    // width combination instead.
  });
}

/** Tourism / heritage POIs — colour-coded by category, plus the tambon
 *  health centroids from the MOPH 3-หมอ register sized by dependent-elderly
 *  load. Both are civic reference layers, not live telemetry. */
export function attractionsLayer(items: Attraction[]) {
  const COLOR: Record<Attraction["category"], [number, number, number]> = {
    dam:       [37, 99, 235],   // --data blue — it's water infrastructure
    viewpoint: [217, 119, 6],   // --gold
    temple:    [217, 119, 6],
    museum:    [99, 102, 241],  // --accent indigo
    park:      [22, 163, 74],   // --good green
    heritage:  [153, 27, 27],   // --crit deep red — irreplaceable
  };
  return new ScatterplotLayer<Attraction>({
    id: "attractions",
    data: items,
    getPosition: (a) => [a.lng, a.lat],
    getRadius: 160,
    radiusMinPixels: 5,
    radiusMaxPixels: 16,
    getFillColor: (a) => [...(COLOR[a.category] ?? [148, 163, 184]), 235],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 2,
    pickable: true,
  });
}

export function healthTambonLayer(items: HealthTambon[]) {
  return new ScatterplotLayer<HealthTambon>({
    id: "health-tambons",
    data: items,
    getPosition: (t) => [t.lng, t.lat],
    // Area encodes population so the circle reads as "how many people".
    getRadius: (t) => Math.sqrt(Math.max(t.population, 1)) * 26,
    radiusMinPixels: 6,
    radiusMaxPixels: 40,
    // Colour encodes dependent-elderly load per 1,000 people — where
    // outreach is hardest, not merely where the most people live.
    getFillColor: (t) => {
      const per1k = t.population > 0
        ? ((t.elderlyBedridden + t.elderlyHomebound) / t.population) * 1000
        : 0;
      if (per1k >= 30) return [153, 27, 27, 210];
      if (per1k >= 20) return [220, 38, 38, 200];
      if (per1k >= 12) return [217, 119, 6, 195];
      return [22, 163, 74, 185];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: true,
  });
}
